import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Package, Wallet, Copy, ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Sparkles, Activity, Camera, Ruler, Receipt, ArrowRightLeft } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, CartesianGrid,
} from "recharts";
import { api, formatApiError } from "../../lib/api";
import InvoiceImportModal from "./InvoiceImportModal";
import DurationEstimatorModal from "./DurationEstimatorModal";
import { ProductInvoicesHistoryModal } from "./AdminStockInvoices";
import StockMovementsModal from "./StockMovementsModal";
import CreateInvoiceFromSelectionModal from "./CreateInvoiceFromSelectionModal";

/**
 * AdminExpenses — two-section monthly ledger: Produtos + Gastos.
 * Rows are inline-editable (name, unit_price, quantity). Subtotal and totals
 * update instantly on the client, and each field save is debounced via blur.
 */
function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function shiftMonth(month, delta) {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(month) {
  const [y, m] = month.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("pt-PT", { year: "numeric", month: "long" });
}

function eur(n) {
  return new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
}

/**
 * Map a 1-10 consumption-rate to a colour + label.
 * 0 = unset (gray). 1-3 green, 4-6 yellow, 7-10 red.
 * The user explicitly asked for: «mínimo verde, médio amarelo, máximo vermelho».
 */
function velocityStyle(rate) {
  const r = Number(rate) || 0;
  if (r === 0)  return { color: "#475569", label: "—",       hint: "Sem classificação" };
  if (r <= 3)   return { color: "#10b981", label: "Lento",   hint: "Gasta-se devagar (verde)" };
  if (r <= 6)   return { color: "#f59e0b", label: "Médio",   hint: "Consumo médio (amarelo)" };
  return            { color: "#ef4444", label: "Rápido",  hint: "Esgota rápido (vermelho)" };
}

/**
 * AdminExpenses — monthly ledger.
 *
 * @prop mode "all" | "produto" | "material" | "gasto" — controls which
 *            section(s) and totals are visible. Defaults to "all"
 *            (legacy combined view). "produto" → /admin/produtos page.
 *            "material" → /admin/materiais. "gasto" → /admin/despesas.
 * @prop title  Override page title. Falls back to a sensible default per mode.
 */
export default function AdminExpenses({ mode = "all", title }) {
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState({ produtos: [], materiais: [], gastos: [], products_total: 0, materials_total: 0, expenses_total: 0, grand_total: 0, products_net: 0, materials_net: 0, expenses_net: 0, vat_total: 0, revenue: 0, cleanings_count: 0, cost_per_cleaning: 0, margin: 0 });
  const [chart, setChart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  const [durationRow, setDurationRow] = useState(null);
  const [historyRow, setHistoryRow] = useState(null);
  const [movementsRow, setMovementsRow] = useState(null);
  const [invoiceFromSelection, setInvoiceFromSelection] = useState(null); // {rows, category}
  const [selectedProdutos, setSelectedProdutos] = useState(new Set());
  const [selectedMateriais, setSelectedMateriais] = useState(new Set());
  const [selectedGastos, setSelectedGastos] = useState(new Set());

  const showProducts = mode === "all" || mode === "produto";
  const showMaterials = mode === "all" || mode === "material";
  const showExpenses = mode === "all" || mode === "gasto";

  // Section-scoped total (com IVA) actually shown to the user.
  const sectionTotal =
    mode === "produto" ? data.products_total
    : mode === "material" ? (data.materials_total || 0)
    : mode === "gasto" ? data.expenses_total
    : data.grand_total;
  const sectionNet =
    mode === "produto" ? (data.products_net || 0)
    : mode === "material" ? (data.materials_net || 0)
    : mode === "gasto" ? (data.expenses_net || 0)
    : (data.products_net || 0) + (data.materials_net || 0) + (data.expenses_net || 0);
  const sectionVat = sectionTotal - sectionNet;

  async function load() {
    setLoading(true);
    try {
      const [{ data: d }, { data: c }] = await Promise.all([
        api.get("/admin/ledger", { params: { month } }),
        api.get("/admin/ledger/chart", { params: { months: 12 } }),
      ]);
      setData(d);
      setChart(c.series || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); setSelectedProdutos(new Set()); setSelectedMateriais(new Set()); setSelectedGastos(new Set()); /* eslint-disable-next-line */ }, [month]);

  async function addRow(category) {
    try {
      const defaultName = category === "produto" ? "Novo produto"
                        : category === "material" ? "Novo material"
                        : "Nova despesa";
      await api.post("/admin/ledger", {
        category,
        name: defaultName,
        unit_price: 0,
        quantity: 0,
        vat_rate: 22,  // taxa normal Região Autónoma da Madeira
        month,
      });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function updateRow(id, patch) {
    try {
      await api.patch(`/admin/ledger/${id}`, patch);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function deleteRow(id, name) {
    if (!window.confirm(`Eliminar "${name}"?`)) return;
    try {
      await api.delete(`/admin/ledger/${id}`);
      toast.success("Item eliminado.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  function bulkCreateInvoice(category) {
    const selected = category === "produto" ? selectedProdutos : selectedMateriais;
    const sourceRows = category === "produto" ? data.produtos : (data.materiais || []);
    const rows = sourceRows.filter(r => selected.has(r.id));
    if (!rows.length) return;
    setInvoiceFromSelection({ rows, category });
  }

  async function bulkDelete(category) {
    const selected = category === "produto" ? selectedProdutos
                   : category === "material" ? selectedMateriais
                   : selectedGastos;
    const ids = Array.from(selected);
    if (!ids.length) return;
    const label = category === "produto" ? "produto(s)"
                : category === "material" ? "material(is)"
                : "despesa(s)";
    if (!window.confirm(`Eliminar ${ids.length} ${label} selecionado(s)? Esta ação é irreversível.`)) return;
    try {
      const { data: r } = await api.post("/admin/ledger/bulk-delete", { ids });
      toast.success(`${r.deleted} ${label} eliminado(s).`);
      if (category === "produto") setSelectedProdutos(new Set());
      else if (category === "material") setSelectedMateriais(new Set());
      else setSelectedGastos(new Set());
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  /**
   * Re-assign selected items between Produtos and Materiais — the admin
   * uses this when a row was imported into the wrong list (e.g. the OCR
   * classified a mop as "Produto"). Data is preserved; only the category
   * flag changes so it appears on the other list immediately.
   */
  async function bulkMove(from) {
    const target = from === "produto" ? "material" : "produto";
    const selected = from === "produto" ? selectedProdutos : selectedMateriais;
    const ids = Array.from(selected);
    if (!ids.length) return;
    const labelFrom = from === "produto" ? "produto(s)" : "material(is)";
    const labelTo = target === "produto" ? "Produtos" : "Materiais";
    if (!window.confirm(`Mover ${ids.length} ${labelFrom} para ${labelTo}? Os dados (nome, código, preço, stock, etc.) são mantidos.`)) return;
    try {
      const { data: r } = await api.post("/admin/ledger/move-category", { ids, target });
      toast.success(`${r.moved} item(s) movido(s) para ${labelTo}.`);
      if (from === "produto") setSelectedProdutos(new Set());
      else setSelectedMateriais(new Set());
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function copyFromPrevious() {
    if (!window.confirm(`Copiar todos os itens do mês anterior para ${monthLabel(month)}?`)) return;
    try {
      const { data: r } = await api.post("/admin/ledger/copy-from-previous", null, { params: { month } });
      toast.success(`${r.copied} itens copiados de ${r.from_month}.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const canCopy = data.produtos.length === 0 && (data.materiais || []).length === 0 && data.gastos.length === 0 && !loading;

  // Headline content varies by mode so each page has its own personality.
  const headline = title
    || (mode === "produto" ? "Produtos"
        : mode === "material" ? "Materiais de Limpeza"
        : mode === "gasto" ? "Despesas"
        : "Produtos & Gastos");
  const description =
    mode === "produto"
      ? "Lista editável mensal de consumíveis (detergentes, panos, luvas, sacos…). O IVA aplica-se por linha conforme as taxas da Região Autónoma da Madeira: reduzida 4%, intermédia 12%, normal 22%."
      : mode === "material"
      ? "Lista editável mensal de materiais de limpeza duráveis (mopas, baldes, vassouras, aspirador, escovas…). Reutilizáveis ao longo do tempo. IVA por linha (Madeira: 4% / 12% / 22%)."
      : mode === "gasto"
      ? "Lista editável mensal de despesas operacionais (combustível, parking, telemóvel, seguros, comissões…). Cada despesa pode ter o seu próprio IVA (Madeira: 4% / 12% / 22%)."
      : "Lista editável mensal. Subtotal = preço × quantidade. O IVA (%) é aplicado por linha conforme as taxas da Madeira (4/12/22) e o Total (com IVA) é o que entra no balanço financeiro.";

  return (
    <div className="space-y-8 max-w-6xl">
      {/* HEADER */}
      <header className="space-y-4" data-testid="expenses-header">
        <div className="eyebrow">Gestão financeira</div>
        <h1 className="font-display text-4xl">{headline}</h1>
        <p className="text-sm text-[#a0b2af] max-w-2xl">{description}</p>

        {/* Month selector + copy from previous */}
        <div className="flex flex-wrap items-center gap-3 pt-2">
          <div className="inline-flex items-center border border-[#1a4640] rounded-sm overflow-hidden">
            <button onClick={() => setMonth(m => shiftMonth(m, -1))}
                    className="px-3 py-2 text-[#a0b2af] hover:text-[#d4af37] hover:bg-[#0a3a33] transition-colors"
                    aria-label="Mês anterior"
                    data-testid="expenses-prev-month">
              <ChevronLeft size={16} />
            </button>
            <div className="px-4 py-2 text-sm font-medium capitalize min-w-[140px] text-center" data-testid="expenses-month-label">
              {monthLabel(month)}
            </div>
            <button onClick={() => setMonth(m => shiftMonth(m, 1))}
                    className="px-3 py-2 text-[#a0b2af] hover:text-[#d4af37] hover:bg-[#0a3a33] transition-colors"
                    aria-label="Mês seguinte"
                    data-testid="expenses-next-month">
              <ChevronRight size={16} />
            </button>
          </div>
          {canCopy && (
            <button onClick={copyFromPrevious}
                    className="px-4 py-2 border border-[#1a4640] rounded-sm text-xs uppercase tracking-widest text-[#a0b2af] hover:border-[#d4af37] hover:text-[#d4af37] transition-colors inline-flex items-center gap-2"
                    data-testid="expenses-copy-prev">
              <Copy size={14} /> Copiar do mês anterior
            </button>
          )}
          {(showProducts || showMaterials) && (
            <button onClick={() => setImportOpen(true)}
                    className="px-4 py-2 border border-[#d4af37]/60 rounded-sm text-xs uppercase tracking-widest text-[#d4af37] hover:bg-[#d4af37] hover:text-[#031c18] transition-colors inline-flex items-center gap-2"
                    data-testid="expenses-import-invoice">
              <Camera size={14} /> Importar de fatura
            </button>
          )}
        </div>
      </header>

      <InvoiceImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        month={month}
        onImported={load}
      />

      <DurationEstimatorModal
        open={!!durationRow}
        row={durationRow}
        onClose={() => setDurationRow(null)}
        onSaved={load}
      />

      {historyRow && (
        <ProductInvoicesHistoryModal
          productId={historyRow.id}
          productName={historyRow.name}
          onClose={() => setHistoryRow(null)}
        />
      )}

      {movementsRow && (
        <StockMovementsModal
          productId={movementsRow.id}
          productName={movementsRow.name}
          onClose={() => setMovementsRow(null)}
        />
      )}

      <CreateInvoiceFromSelectionModal
        open={!!invoiceFromSelection}
        rows={invoiceFromSelection?.rows || []}
        onClose={() => setInvoiceFromSelection(null)}
        onCreated={() => {
          const cat = invoiceFromSelection?.category;
          if (cat === "produto") setSelectedProdutos(new Set());
          else if (cat === "material") setSelectedMateriais(new Set());
          setInvoiceFromSelection(null);
          load();
        }}
      />

      {/* PRODUTOS */}
      {showProducts && (
        <ExpenseTable
          title="Produtos"
          subtitle="Consumíveis de limpeza · detergentes, panos, luvas, sacos…"
          icon={<Package size={18} />}
          category="produto"
          rows={data.produtos}
          total={data.products_total}
          loading={loading}
          onAdd={() => addRow("produto")}
          onUpdate={updateRow}
          onDelete={deleteRow}
          onOpenDuration={setDurationRow}
          onOpenHistory={setHistoryRow}
          onOpenMovements={setMovementsRow}
          testPrefix="produtos"
          selectedIds={selectedProdutos}
          onToggleSelect={(id) => setSelectedProdutos(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
          })}
          onSelectAll={(allIds) => setSelectedProdutos(prev => {
            const everySelected = allIds.length > 0 && allIds.every(id => prev.has(id));
            return everySelected ? new Set() : new Set(allIds);
          })}
          onBulkDelete={() => bulkDelete("produto")}
          onBulkCreateInvoice={() => bulkCreateInvoice("produto")}
          onBulkMove={() => bulkMove("produto")}
          moveLabel="Mover para Materiais"
        />
      )}

      {/* MATERIAIS */}
      {showMaterials && (
        <ExpenseTable
          title="Materiais"
          subtitle="Materiais de limpeza duráveis · mopas, baldes, vassouras, aspirador, escovas…"
          icon={<Package size={18} />}
          category="material"
          rows={data.materiais || []}
          total={data.materials_total || 0}
          loading={loading}
          onAdd={() => addRow("material")}
          onUpdate={updateRow}
          onDelete={deleteRow}
          onOpenDuration={setDurationRow}
          onOpenHistory={setHistoryRow}
          onOpenMovements={setMovementsRow}
          testPrefix="materiais"
          selectedIds={selectedMateriais}
          onToggleSelect={(id) => setSelectedMateriais(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
          })}
          onSelectAll={(allIds) => setSelectedMateriais(prev => {
            const everySelected = allIds.length > 0 && allIds.every(id => prev.has(id));
            return everySelected ? new Set() : new Set(allIds);
          })}
          onBulkDelete={() => bulkDelete("material")}
          onBulkCreateInvoice={() => bulkCreateInvoice("material")}
          onBulkMove={() => bulkMove("material")}
          moveLabel="Mover para Produtos"
        />
      )}

      {/* GASTOS */}
      {showExpenses && (
        <ExpenseTable
          title="Despesas"
          subtitle="Despesas operacionais · combustível, parking, telemóvel, seguros, comissões…"
          icon={<Wallet size={18} />}
          category="gasto"
          rows={data.gastos}
          total={data.expenses_total}
          loading={loading}
          onAdd={() => addRow("gasto")}
          onUpdate={updateRow}
          onDelete={deleteRow}
          testPrefix="gastos"
          selectedIds={selectedGastos}
          onToggleSelect={(id) => setSelectedGastos(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
          })}
          onSelectAll={(allIds) => setSelectedGastos(prev => {
            const everySelected = allIds.length > 0 && allIds.every(id => prev.has(id));
            return everySelected ? new Set() : new Set(allIds);
          })}
          onBulkDelete={() => bulkDelete("gasto")}
        />
      )}

      {/* KPIs + GRAND TOTAL */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="expenses-kpis">
        <KpiCard
          label="Limpezas do mês"
          value={data.cleanings_count}
          icon={<Activity size={16} />}
          testId="kpi-cleanings" />
        <KpiCard
          label="Receita"
          value={eur(data.revenue)}
          icon={<TrendingUp size={16} />}
          positive
          testId="kpi-revenue" />
        <KpiCard
          label="Custo / limpeza"
          value={data.cleanings_count ? eur(data.cost_per_cleaning) : "—"}
          hint={data.cleanings_count ? `${eur(data.grand_total)} ÷ ${data.cleanings_count}` : "Sem limpezas concluídas"}
          icon={<Sparkles size={16} />}
          testId="kpi-cost-per-cleaning" />
        <KpiCard
          label="Margem do mês"
          value={eur(data.margin)}
          positive={data.margin >= 0}
          negative={data.margin < 0}
          icon={data.margin >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
          testId="kpi-margin" />
      </section>

      <section className="lux-card p-6 border-[#d4af37]/40" data-testid="expenses-grand-total">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="eyebrow text-[#d4af37]">
              {mode === "produto" ? "Total de produtos do mês (com IVA)"
                : mode === "material" ? "Total de materiais do mês (com IVA)"
                : mode === "gasto" ? "Total de despesas do mês (com IVA)"
                : "Total de custos do mês (com IVA)"}
            </div>
            <div className="text-xs text-[#a0b2af] mt-1 capitalize">
              {monthLabel(month)}
              {mode === "all" && <> · Produtos {eur(data.products_total)} + Materiais {eur(data.materials_total || 0)} + Gastos {eur(data.expenses_total)}</>}
            </div>
            {sectionVat > 0 && (
              <div className="text-xs text-[#a0b2af] mt-1.5" data-testid="expenses-vat-breakdown">
                Líquido {eur(sectionNet)}
                {" + "}
                <span className="text-[#d4af37]">IVA {eur(sectionVat)}</span>
              </div>
            )}
          </div>
          <div className="text-4xl font-display text-[#d4af37]" data-testid="expenses-grand-total-value">
            {eur(sectionTotal)}
          </div>
        </div>
      </section>

      {/* CHARTS */}
      <ChartsSection series={chart} />
    </div>
  );
}

function KpiCard({ label, value, hint, icon, positive, negative, testId }) {
  const valueColor = positive ? "text-[#10b981]" : negative ? "text-[#fca5a5]" : "text-[#fdfbf7]";
  return (
    <div className="lux-card p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-[#a0b2af] text-[10px] uppercase tracking-widest">
        <span className="text-[#d4af37]">{icon}</span>
        {label}
      </div>
      <div className={`mt-2 font-display text-2xl ${valueColor}`}>{value}</div>
      {hint && <div className="text-[10px] text-[#a0b2af] mt-1">{hint}</div>}
    </div>
  );
}

function ChartsSection({ series }) {
  if (!series.length) return null;
  // Slice to last 6 months for readability on mobile (still shows 12 on wide screens via responsive container)
  const shortLabel = (mk) => {
    const [y, m] = mk.split("-");
    const d = new Date(Number(y), Number(m) - 1, 1);
    return d.toLocaleDateString("pt-PT", { month: "short" }).replace(".", "");
  };
  const data = series.map(s => ({ ...s, label: shortLabel(s.month) }));
  const tooltipStyle = {
    backgroundColor: "#041f1b",
    border: "1px solid #1a4640",
    borderRadius: 2,
    padding: "8px 12px",
    fontSize: 12,
  };
  const fmtEur = (v) => `${Number(v || 0).toFixed(0)} €`;

  return (
    <div className="space-y-8" data-testid="expenses-charts">
      <section className="lux-card p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="eyebrow">Evolução mensal</div>
            <div className="font-display text-xl mt-1">Receita vs Custos · 12 meses</div>
          </div>
        </div>
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1a4640" />
              <XAxis dataKey="label" tick={{ fill: "#a0b2af", fontSize: 11 }} axisLine={{ stroke: "#1a4640" }} />
              <YAxis tickFormatter={fmtEur} tick={{ fill: "#a0b2af", fontSize: 11 }} axisLine={{ stroke: "#1a4640" }} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v, name) => [fmtEur(v), name]} cursor={{ fill: "#ffffff08" }} />
              <Legend wrapperStyle={{ fontSize: 11, color: "#a0b2af" }} />
              <Bar dataKey="revenue" fill="#10b981" name="Receita" radius={[2, 2, 0, 0]} />
              <Bar dataKey="expenses" fill="#d4af37" name="Custos" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="lux-card p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="eyebrow">Eficiência operacional</div>
            <div className="font-display text-xl mt-1">Custo por limpeza · 12 meses</div>
          </div>
        </div>
        <div style={{ width: "100%", height: 240 }}>
          <ResponsiveContainer>
            <LineChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1a4640" />
              <XAxis dataKey="label" tick={{ fill: "#a0b2af", fontSize: 11 }} axisLine={{ stroke: "#1a4640" }} />
              <YAxis tickFormatter={fmtEur} tick={{ fill: "#a0b2af", fontSize: 11 }} axisLine={{ stroke: "#1a4640" }} />
              <Tooltip contentStyle={tooltipStyle}
                       formatter={(v, name) => name === "Limpezas" ? [v, name] : [fmtEur(v), name]} />
              <Legend wrapperStyle={{ fontSize: 11, color: "#a0b2af" }} />
              <Line type="monotone" dataKey="cost_per_cleaning" stroke="#d4af37" strokeWidth={2}
                    dot={{ fill: "#d4af37", r: 3 }} name="Custo / limpeza" />
              <Line type="monotone" dataKey="cleanings" stroke="#a0b2af" strokeWidth={1.5}
                    dot={{ fill: "#a0b2af", r: 2 }} name="Limpezas" yAxisId="right" />
              <YAxis yAxisId="right" orientation="right" tick={{ fill: "#a0b2af", fontSize: 11 }} axisLine={{ stroke: "#1a4640" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>
    </div>
  );
}

function ExpenseTable({ title, subtitle, icon, category, rows, total, loading, onAdd, onUpdate, onDelete, onOpenDuration, onOpenHistory, onOpenMovements, testPrefix, selectedIds, onToggleSelect, onSelectAll, onBulkDelete, onBulkCreateInvoice, onBulkMove, moveLabel }) {
  // Treat `material` rows the same as `produto` for layout (durations, velocity, code).
  const isProdutos = category === "produto" || category === "material";
  // Grid columns adapt: produtos have extra "Stock" + "Velocidade" columns.
  // Order: select · # · Stock · Código · Nome · Preço · Qtd · IVA% · Unit c/IVA · Subtotal · Total [· Velocidade] · ações
  const gridCols = isProdutos
    ? "grid-cols-[28px_36px_70px_minmax(80px,0.7fr)_minmax(220px,3fr)_minmax(70px,0.6fr)_minmax(50px,0.4fr)_minmax(60px,0.5fr)_minmax(80px,0.6fr)_minmax(80px,0.6fr)_minmax(80px,0.6fr)_minmax(150px,0.9fr)_108px]"
    : "grid-cols-[28px_36px_minmax(80px,0.7fr)_minmax(240px,3fr)_minmax(70px,0.6fr)_minmax(50px,0.4fr)_minmax(60px,0.5fr)_minmax(80px,0.6fr)_minmax(80px,0.6fr)_minmax(80px,0.6fr)_28px]";
  const minWidth = isProdutos ? 1185 : 960;
  const allIds = rows.map(r => r.id);
  const selCount = allIds.filter(id => selectedIds && selectedIds.has(id)).length;
  const allSelected = allIds.length > 0 && selCount === allIds.length;
  const someSelected = selCount > 0 && !allSelected;
  return (
    <section className="space-y-3" data-testid={`expenses-section-${testPrefix}`}>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-[#d4af37]">
            {icon}
            <div className="font-display text-2xl">{title}</div>
          </div>
          <p className="text-xs text-[#a0b2af] mt-1 max-w-xl">{subtitle}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {selCount > 0 && (
            <>
              {onBulkCreateInvoice && (
                <button onClick={onBulkCreateInvoice}
                        className="px-4 py-2 border border-[#d4af37]/60 rounded-sm text-xs uppercase tracking-widest text-[#d4af37] hover:bg-[#d4af37] hover:text-[#031c18] transition-colors inline-flex items-center gap-2"
                        data-testid={`expenses-bulk-invoice-${testPrefix}`}
                        title="Criar uma nova entrada de stock com os produtos selecionados">
                  <Receipt size={14} /> Criar fatura com {selCount} selecionado(s)
                </button>
              )}
              {onBulkMove && (
                <button onClick={onBulkMove}
                        className="px-4 py-2 border border-[#60a5fa]/60 rounded-sm text-xs uppercase tracking-widest text-[#60a5fa] hover:bg-[#60a5fa] hover:text-[#031c18] transition-colors inline-flex items-center gap-2"
                        data-testid={`expenses-bulk-move-${testPrefix}`}
                        title="Mover os itens selecionados para a outra lista (dados preservados).">
                  <ArrowRightLeft size={14} /> {moveLabel || "Mover"} ({selCount})
                </button>
              )}
              <button onClick={onBulkDelete}
                      className="px-4 py-2 border border-[#ef4444]/60 rounded-sm text-xs uppercase tracking-widest text-[#ef4444] hover:bg-[#ef4444] hover:text-[#fdfbf7] transition-colors inline-flex items-center gap-2"
                      data-testid={`expenses-bulk-delete-${testPrefix}`}>
                <Trash2 size={14} /> Eliminar {selCount} selecionado(s)
              </button>
            </>
          )}
          <button onClick={onAdd}
                  className="btn-ghost-gold px-4 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2"
                  data-testid={`expenses-add-${testPrefix}`}>
            <Plus size={14} /> Adicionar linha
          </button>
        </div>
      </div>

      <div className="lux-card overflow-hidden">
        <div className="overflow-x-auto" data-testid={`expenses-scroll-${testPrefix}`}>
          <div style={{ minWidth }}>
            <div className={`grid ${gridCols} gap-2 px-4 py-3 border-b border-[#1a4640] bg-[#041f1b] text-[10px] uppercase tracking-widest text-[#a0b2af]`}>
              <div className="flex items-center justify-center">
                <input type="checkbox"
                       checked={allSelected}
                       ref={el => { if (el) el.indeterminate = someSelected; }}
                       onChange={() => onSelectAll && onSelectAll(allIds)}
                       className="accent-[#d4af37] w-3.5 h-3.5 cursor-pointer"
                       title={allSelected ? "Desmarcar todos" : "Selecionar todos"}
                       data-testid={`expenses-select-all-${testPrefix}`} />
              </div>
              <div className="text-center">#</div>
              {isProdutos && <div className="text-right" title="Stock atual (acumulado pelas entradas em «Stock de produtos»)">Stock</div>}
              <div>Código</div>
              <div>Nome</div>
              <div className="text-right">Preço unit.</div>
              <div className="text-right">Qtd</div>
              <div className="text-right" title="Taxas Madeira: reduzida 4%, intermédia 12%, normal 22%.">IVA %</div>
              <div className="text-right" title="Preço unitário com IVA = preço unit. × (1 + IVA/100)">Unit. c/IVA</div>
              <div className="text-right">Subtotal</div>
              <div className="text-right">Total</div>
              {isProdutos && <div className="text-center" title="Escala 1-10. Verde=lento, Amarelo=médio, Vermelho=rápido.">Velocidade</div>}
              <div></div>
            </div>

            {loading ? (
              <div className="px-4 py-10 text-center text-sm text-[#a0b2af]">A carregar…</div>
            ) : rows.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-[#a0b2af]" data-testid={`expenses-empty-${testPrefix}`}>
                Sem {title.toLowerCase()} registados neste mês. Clica <span className="text-[#d4af37]">Adicionar linha</span> para começar.
              </div>
            ) : (
              <div className="divide-y divide-[#1a4640]">
                {rows.map((r, idx) => (
                  <Row key={r.id} row={r} index={idx + 1} onUpdate={onUpdate} onDelete={onDelete}
                       testPrefix={testPrefix} gridCols={gridCols} isProdutos={isProdutos}
                       onOpenDuration={onOpenDuration}
                       onOpenHistory={onOpenHistory}
                       onOpenMovements={onOpenMovements}
                       selected={selectedIds && selectedIds.has(r.id)}
                       onToggleSelect={onToggleSelect} />
                ))}
              </div>
            )}

            <div className={`grid ${gridCols} gap-2 px-4 py-3 border-t border-[#1a4640] bg-[#041f1b] items-center`}>
              <div className="text-xs uppercase tracking-widest text-[#a0b2af] text-right"
                   style={{ gridColumn: `1 / span ${isProdutos ? 11 : 9}` }}>
                Total {title} (com IVA)
              </div>
              <div className="text-right font-display text-xl text-[#d4af37]"
                   data-testid={`expenses-total-${testPrefix}`}>
                {eur(total)}
              </div>
              {isProdutos && <div></div>}
              <div></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Row({ row, index, onUpdate, onDelete, testPrefix, gridCols, isProdutos, onOpenDuration, onOpenHistory, onOpenMovements, selected, onToggleSelect }) {
  const [name, setName] = useState(row.name);
  const [code, setCode] = useState(row.code ?? "");
  const [unitPrice, setUnitPrice] = useState(row.unit_price);
  const [quantity, setQuantity] = useState(row.quantity);
  const [vatRate, setVatRate] = useState(row.vat_rate ?? 0);
  const [rate, setRate] = useState(row.consumption_rate ?? 0);
  const [stockQty, setStockQty] = useState(row.stock_quantity ?? 0);

  useEffect(() => {
    setName(row.name);
    setCode(row.code ?? "");
    setUnitPrice(row.unit_price);
    setQuantity(row.quantity);
    setVatRate(row.vat_rate ?? 0);
    setRate(row.consumption_rate ?? 0);
    setStockQty(row.stock_quantity ?? 0);
  }, [row.name, row.code, row.unit_price, row.quantity, row.vat_rate, row.consumption_rate, row.stock_quantity]);

  const subtotal = useMemo(
    () => (Number(unitPrice) || 0) * (Number(quantity) || 0),
    [unitPrice, quantity],
  );
  const unitWithVat = useMemo(
    () => (Number(unitPrice) || 0) * (1 + (Number(vatRate) || 0) / 100),
    [unitPrice, vatRate],
  );
  const total = useMemo(
    () => subtotal * (1 + (Number(vatRate) || 0) / 100),
    [subtotal, vatRate],
  );
  const dirty = name !== row.name
    || Number(unitPrice) !== Number(row.unit_price)
    || Number(quantity) !== Number(row.quantity)
    || Number(vatRate) !== Number(row.vat_rate ?? 0)
    || Number(rate) !== Number(row.consumption_rate ?? 0);

  function commit(partial) {
    if (Object.keys(partial).length === 0) return;
    onUpdate(row.id, partial);
  }

  return (
    <div className={`grid ${gridCols} gap-2 px-4 py-2 items-center transition-colors ${selected ? "bg-[#d4af37]/10" : "hover:bg-[#0a3a33]/30"}`}
         data-testid={`expenses-row-${testPrefix}-${row.id}`}>
      <div className="flex items-center justify-center">
        <input type="checkbox"
               checked={!!selected}
               onChange={() => onToggleSelect && onToggleSelect(row.id)}
               className="accent-[#d4af37] w-3.5 h-3.5 cursor-pointer"
               aria-label="Selecionar linha"
               data-testid={`expenses-row-select-${row.id}`} />
      </div>
      <div className="text-center text-xs font-display text-[#a0b2af] tabular-nums"
           data-testid={`expenses-row-index-${row.id}`}>
        {index}
      </div>
      {isProdutos && (
        <div className="relative">
          <input
            type="number"
            step="0.01"
            min="0"
            value={stockQty}
            onChange={e => setStockQty(e.target.value)}
            onBlur={() => {
              const v = Number(stockQty) || 0;
              if (v !== Number(row.stock_quantity || 0)) onUpdate(row.id, { stock_quantity: v });
            }}
            className="w-full bg-transparent border border-transparent focus:border-[#d4af37] focus:bg-[#062c27] rounded-sm pl-6 pr-1.5 py-1 text-sm text-right tabular-nums outline-none transition-colors"
            style={{ color: Number(stockQty || 0) === 0 ? "#a0b2af" : "#10b981" }}
            title="Stock atual (editável). Atualizado pelas entradas de «Stock de produtos» mas também pode ser corrigido manualmente."
            data-testid={`expenses-row-stock-${row.id}`}
          />
          {row.stock_status === "critical" && (
            <span
              className="absolute left-1.5 top-1/2 -translate-y-1/2 inline-block w-2 h-2 rounded-full bg-[#ef4444] animate-pulse"
              title={`Rutura de stock · consumo estimado ${row.weekly_need}/semana`}
              aria-label="Rutura de stock"
              data-testid={`stock-badge-critical-${row.id}`}
            />
          )}
          {row.stock_status === "low" && (
            <span
              className="absolute left-1.5 top-1/2 -translate-y-1/2 inline-block w-2 h-2 rounded-full bg-[#f59e0b]"
              title={`Stock baixo · consumo estimado ${row.weekly_need}/semana`}
              aria-label="Stock baixo"
              data-testid={`stock-badge-low-${row.id}`}
            />
          )}
        </div>
      )}
      <input
        value={code}
        onChange={e => setCode(e.target.value)}
        onBlur={() => {
          const v = (code || "").trim().slice(0, 40);
          if (v !== (row.code || "")) onUpdate(row.id, { code: v });
        }}
        placeholder="—"
        title="Código / SKU do produto (opcional)"
        className="bg-transparent border border-transparent focus:border-[#d4af37] focus:bg-[#062c27] rounded-sm px-2 py-1.5 text-xs font-mono text-[#d4af37] outline-none transition-colors w-full"
        data-testid={`expenses-row-code-${row.id}`}
      />
      <input
        value={name}
        onChange={e => setName(e.target.value)}
        onBlur={() => { if (name.trim() && name !== row.name) commit({ name: name.trim() }); else if (!name.trim()) setName(row.name); }}
        title={name}
        className="bg-transparent border border-transparent focus:border-[#d4af37] focus:bg-[#062c27] rounded-sm px-2 py-1.5 text-sm text-[#fdfbf7] outline-none transition-colors w-full"
        data-testid={`expenses-row-name-${row.id}`}
      />
      <div className="relative">
        <input
          type="number" step="0.01" min="0" inputMode="decimal"
          value={unitPrice}
          onChange={e => setUnitPrice(e.target.value)}
          onBlur={() => { const n = parseFloat(unitPrice); if (Number.isFinite(n) && n !== Number(row.unit_price)) commit({ unit_price: n }); else if (!Number.isFinite(n)) setUnitPrice(row.unit_price); }}
          className="w-full bg-transparent border border-transparent focus:border-[#d4af37] focus:bg-[#062c27] rounded-sm pl-2 pr-6 py-1.5 text-sm text-right outline-none transition-colors"
          data-testid={`expenses-row-price-${row.id}`}
        />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[#a0b2af] pointer-events-none">€</span>
      </div>
      <input
        type="number" step="0.01" min="0" inputMode="decimal"
        value={quantity}
        onChange={e => setQuantity(e.target.value)}
        onBlur={() => { const n = parseFloat(quantity); if (Number.isFinite(n) && n !== Number(row.quantity)) commit({ quantity: n }); else if (!Number.isFinite(n)) setQuantity(row.quantity); }}
        className="bg-transparent border border-transparent focus:border-[#d4af37] focus:bg-[#062c27] rounded-sm px-2 py-1.5 text-sm text-right outline-none transition-colors min-w-0"
        data-testid={`expenses-row-qty-${row.id}`}
      />
      <div className="relative">
        <input
          type="number" step="0.5" min="0" max="100" inputMode="decimal"
          value={vatRate}
          onChange={e => setVatRate(e.target.value)}
          onBlur={() => {
            const n = parseFloat(vatRate);
            if (Number.isFinite(n) && n >= 0 && n <= 100 && n !== Number(row.vat_rate ?? 0)) commit({ vat_rate: n });
            else if (!Number.isFinite(n) || n < 0 || n > 100) setVatRate(row.vat_rate ?? 0);
          }}
          className="w-full bg-transparent border border-transparent focus:border-[#d4af37] focus:bg-[#062c27] rounded-sm pl-2 pr-5 py-1.5 text-sm text-right outline-none transition-colors"
          data-testid={`expenses-row-vat-${row.id}`}
          title="Taxa de IVA aplicada a esta linha"
        />
        <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-[#a0b2af] pointer-events-none">%</span>
      </div>
      <div className="text-right text-sm tabular-nums text-[#fdfbf7]"
           data-testid={`expenses-row-unit-with-vat-${row.id}`}
           title={`Preço unitário com IVA (${vatRate || 0}%)`}>
        {eur(unitWithVat)}
      </div>
      <div className="text-right text-sm tabular-nums text-[#a0b2af]"
           data-testid={`expenses-row-subtotal-${row.id}`}
           title="Subtotal sem IVA">
        {eur(subtotal)}
      </div>
      <div className={`text-right text-sm tabular-nums font-medium ${dirty ? "text-[#d4af37]" : "text-[#fdfbf7]"}`}
           data-testid={`expenses-row-total-${row.id}`}
           title={`Total com IVA (${vatRate || 0}%)`}>
        {eur(total)}
      </div>
      {isProdutos && (
        <VelocityCell
          rate={rate}
          onChange={(n) => {
            setRate(n);
            if (n !== Number(row.consumption_rate ?? 0)) commit({ consumption_rate: n });
          }}
          rowId={row.id}
        />
      )}
      <div className="flex justify-end items-center gap-1">
        {isProdutos && onOpenMovements && (
          <button onClick={() => onOpenMovements(row)}
                  className="p-1.5 text-[#a0b2af] hover:text-[#d4af37] transition-colors"
                  aria-label="Movimentos de stock"
                  title="Movimentos de stock · histórico + tendência"
                  data-testid={`expenses-row-movements-${row.id}`}>
            <Activity size={14} />
          </button>
        )}
        {isProdutos && onOpenHistory && (
          <button onClick={() => onOpenHistory(row)}
                  className="p-1.5 text-[#a0b2af] hover:text-[#d4af37] transition-colors"
                  aria-label="Histórico de faturas"
                  title="Histórico de faturas deste produto"
                  data-testid={`expenses-row-history-${row.id}`}>
            <Receipt size={14} />
          </button>
        )}
        {isProdutos && onOpenDuration && (
          <button onClick={() => onOpenDuration(row)}
                  className="p-1.5 text-[#a0b2af] hover:text-[#d4af37] transition-colors"
                  aria-label="Estimativa de duração"
                  title="Estimativa de duração"
                  data-testid={`expenses-row-duration-${row.id}`}>
            <Ruler size={14} />
          </button>
        )}
        <button onClick={() => onDelete(row.id, row.name)}
                className="p-1.5 text-[#a0b2af] hover:text-[#ef4444] transition-colors"
                aria-label="Eliminar"
                data-testid={`expenses-row-delete-${row.id}`}>
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

/**
 * Tiny 10-segment bar to manually set a product's consumption speed.
 * Click any segment → that value is committed. Color follows velocityStyle().
 */
function VelocityCell({ rate, onChange, rowId }) {
  const vel = velocityStyle(rate);
  return (
    <div className="flex items-center gap-2"
         data-testid={`expenses-row-velocity-${rowId}`}
         title={vel.hint}>
      <div className="flex gap-[2px]">
        {[1,2,3,4,5,6,7,8,9,10].map(n => {
          const active = n <= (Number(rate) || 0);
          const segColor = velocityStyle(n).color;
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange(rate === n ? 0 : n)}
              className="w-2 h-4 rounded-[1px] transition-all hover:scale-110"
              style={{
                background: active ? segColor : "#062c27",
                border: `1px solid ${active ? segColor : "#1a4640"}`,
              }}
              aria-label={`Velocidade ${n}/10`}
              data-testid={`expenses-row-velocity-seg-${rowId}-${n}`}
            />
          );
        })}
      </div>
      <span className="text-[10px] uppercase tracking-widest"
            style={{ color: vel.color, minWidth: 50 }}>
        {Number(rate) ? `${rate} · ${vel.label}` : "—"}
      </span>
    </div>
  );
}
